import { cleanSrc, mergeRefs } from '@sohanemon/utils';
import { m } from '@sohanscript/i18n';
import type * as React from 'react';
import { cacheKey, preloadedImages } from './img-utils';

export type ImgProps = React.ImgHTMLAttributes<HTMLImageElement> & {
	src: string;
	width?: number;
	height?: number;
	alt?: string;
	placeholder?: 'none' | 'shimmer' | (string & {});
	sizes?: string;
	ref?: React.Ref<HTMLImageElement>;
};

/**
 * Base image renderer: alt fallback, preload tracking, lazy loading and the
 * placeholder background. CDN resizing (`srcSet`) stays in the app layer.
 */
export function Img({
	src,
	width,
	height,
	alt,
	placeholder,
	sizes,
	style,
	onLoad,
	ref,
	...props
}: ImgProps) {
	const fallbackAlt = src.substring(src.lastIndexOf('/') + 1).slice(0, 20);
	const resolvedAlt = (alt ?? fallbackAlt) || m.image_default_alt();
	const resolvedSrc = cleanSrc(src);

	return (
		<img
			ref={mergeRefs(ref, (el) => {
				if (el && preloadedImages.has(cacheKey(resolvedSrc)))
					el.setAttribute('data-loaded', 'true');
			})}
			src={resolvedSrc}
			width={width}
			height={height ?? width}
			alt={resolvedAlt}
			draggable={false}
			loading="lazy"
			decoding="async"
			sizes={sizes}
			onLoad={(event) => {
				preloadedImages.add(cacheKey(resolvedSrc));
				event.currentTarget.setAttribute('data-loaded', 'true');
				onLoad?.(event);
			}}
			style={{
				backgroundImage:
					placeholder && placeholder !== 'none'
						? `url(${placeholder})`
						: undefined,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
				...style,
			}}
			{...props}
		/>
	);
}
